import {
  Close,
  ETooltipVariant,
  GeneralAlertTriangleOutlineIcon,
  Tooltip,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useEffect, useRef } from 'react';
import { useFocusRing } from 'react-aria';
import { useIntl } from 'react-intl';

import { useForwardedRef } from '@/hooks/useForwardedRef';

interface ILocationSearchInputProps {
  getInputProps: () => Record<string, never>;
  getInputLabelProps: () => Record<string, string>;
  // Keyboard handling for the whole field, owned by the parent: the arrow keys move
  // into the suggestions, and Tab does too once it runs out of controls here. It is
  // attached to the field wrapper rather than the input because the clear button is
  // part of the field and Bonfire's Close accepts no key props.
  onFieldKeyDown?: (event: KeyboardEvent) => void;
  // Called whenever the field takes focus, which is what opens its popup. This is
  // the field's own focus event rather than the autocomplete's `focused` state: that
  // state goes stale, because moving focus into the popup deliberately skips the
  // autocomplete's blur handling (see onBlur below), so it never flips back to
  // false and a second visit to the field would look like no change at all.
  onFieldFocus?: () => void;
  // Whether the suggestions listbox is actually on screen. The input only claims
  // aria-expanded / aria-controls while it is — the popup is open but empty until
  // there are options to show.
  expanded?: boolean;
  alertStatus?: boolean;
  flexible?: boolean;
  redesigned?: boolean;
}

const LocationSearchInput: React.FC<ILocationSearchInputProps> = ({
  getInputProps,
  getInputLabelProps,
  onFieldKeyDown,
  onFieldFocus,
  expanded = false,
  alertStatus,
  flexible,
  redesigned,
}) => {
  const intl = useIntl();
  // `isTextInput` keeps react-aria from raising the ring while the user types.
  const { isFocusVisible, focusProps } = useFocusRing({ isTextInput: true });

  const allInputProps = getInputProps() as {
    onFocus?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    value?: string;
    ref?: React.Ref<HTMLInputElement>;
    'aria-controls'?: string;
    'aria-activedescendant'?: string;
  };

  const {
    onFocus,
    onChange,
    value,
    ref: materialUIRef,
    onBlur: onAutocompleteBlur,
    'aria-controls': ariaControls,
    // Dropped rather than forwarded. The autocomplete emits an empty
    // aria-activedescendant while its popup is open, which only means anything if the
    // active option is tracked while focus stays in the input. Focus moves into the
    // list here, so forwarding it would advertise two combobox models at once.
    'aria-activedescendant': _ariaActiveDescendant,
    ...inputProps
  } = allInputProps;
  const { internalRef, forwardedRef } = useForwardedRef<HTMLInputElement>(materialUIRef);

  // Native listener on the field wrapper: a React onKeyDown there would be a key
  // handler on a plain div, which jsx-a11y rightly objects to, and the events we want
  // come from the input and the clear button inside it either way.
  const fieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const field = fieldRef.current;
    if (!field || !onFieldKeyDown) return;

    field.addEventListener('keydown', onFieldKeyDown);
    return () => field.removeEventListener('keydown', onFieldKeyDown);
  }, [onFieldKeyDown]);

  const handleClear = () => {
    onChange?.({ target: { value: '' } } as any);
    internalRef.current?.focus();
  };

  const getLocationPlaceholder = () => {
    return intl.formatMessage({
      defaultMessage: 'Location',
      id: 'cGCkb9',
      description: 'Global Header > Placeholder text for header search field',
    });
  };

  const locationPlaceholder = getLocationPlaceholder();

  // The placeholder disappears the moment the field has a value, so the input
  // needs a real label of its own. It is visually hidden — the magnifier and the
  // placeholder already carry the meaning for sighted users — and doubles as the
  // target of the listbox's aria-labelledby, which MUI points at this id.
  const alertMessage = intl.formatMessage({
    defaultMessage: 'Enter a delivery address for the best results.',
    id: 'b9wuaC',
  });

  const locationLabel = intl.formatMessage({
    defaultMessage: 'Search destinations',
    id: 'JXjIqt',
    description: 'Global Header > Search > Label for the location search field',
  });

  return (
    <div
      className={
        flexible
          ? 'flex-1 min-w-[130px] rounded-tl-lg rounded-bl-lg'
          : 'rounded-tl-lg rounded-bl-lg'
      }>
      {/* Bonfire's `.focusRing`, same as the tabs in SearchTabs. It goes on the
          field rather than on the <input>, which only spans the text inside the
          field's padding, so all three fields ring the same way. */}
      <div
        ref={fieldRef}
        data-location-field
        className={clsx(
          'relative flex flex-row items-center h-12 rounded-full',
          isFocusVisible && 'focusRing',
          flexible ? 'w-full' : 'w-72',
        )}>
        <label {...getInputLabelProps()} className="sr-only">
          {locationLabel}
        </label>
        <div className="flex items-center w-full h-full py-2 pl-4 pr-10 text-gray-900">
          <input
            ref={forwardedRef}
            className={`w-full text-base font-medium text-left text-gray-900 truncate bg-transparent semiHighlight focus:outline-none ${redesigned ? 'placeholder:text-secondary-base' : 'placeholder:text-gray-800'}`}
            data-testid="add-location-input"
            placeholder={locationPlaceholder}
            onFocus={e => {
              focusProps.onFocus?.(e);
              onFocus?.();
              onFieldFocus?.();
              e.target.select();
            }}
            onChange={onChange}
            value={value}
            {...inputProps}
            onBlur={e => {
              // Keep the autocomplete's own blur handling — it closes the popup —
              // and drop the text selection made on focus.
              focusProps.onBlur?.(e);
              // Except when focus is moving into the popup: the autocomplete reads
              // that as focus escaping the widget and pulls it straight back to the
              // input, which would make the suggestions impossible to Tab into.
              if (!(e.relatedTarget as HTMLElement | null)?.closest('[role="listbox"]')) {
                onAutocompleteBlur?.(e);
              }
              window.getSelection()?.removeAllRanges();
            }}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={expanded}
            aria-controls={expanded ? ariaControls : undefined}
          />
        </div>
        {value && (
          <div className="absolute flex items-center justify-center right-2.5">
            <Close
              onClose={handleClear}
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Clear search',
                id: 'IEjI3e',
                description: 'Global Header > Search > Clear the location field',
              })}
            />
          </div>
        )}
        {alertStatus && (
          <>
            {/* The tooltip only opens on hover, so its text never reaches the
                accessibility tree and the icon was the sole carrier of the warning.
                Put the warning in text for assistive tech and hide the icon, which
                now only repeats it. */}
            <span className="sr-only">{alertMessage}</span>
            <Tooltip
              content={alertMessage}
              className="absolute right-0 z-50 hidden w-4 h-4 text-gray-900 md:block"
              placement="bottom-start"
              variant={ETooltipVariant.Dark}
              arrow={false}>
              <GeneralAlertTriangleOutlineIcon aria-hidden />
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationSearchInput;
